import React from 'react';
import { colors } from '@styles/colors';

const cardBaseStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: 'clamp(12px, 2vh, 20px) clamp(12px, 2.2vh, 20px)',
  borderRadius: '1.2vh',
  backgroundColor: colors.white,
  boxShadow: '0 0.4vh 1.2vh rgba(0,0,0,0.08)',
  border: `1px solid ${colors.gray200}`,
};

const labelBase = {
  fontFamily: 'Inter',
  fontWeight: 600,
  fontSize: 'clamp(12px, 1.4vh, 16px)',
  color: colors.gray600,
  minWidth: 0,
};

const valueBase = {
  fontFamily: 'Inter',
  fontSize: 'clamp(13px, 1.6vh, 18px)',
  color: colors.primaryText,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  minWidth: 0,
};

/**
 * Una fila dentro de DataCard.
 * layout "inline": etiqueta a la izquierda, valor a la derecha.
 * layout "stack": etiqueta arriba, valor debajo a ancho completo (útil para botones / acciones).
 */
function DataCardRow({ label, value, layout = 'inline', isLast }) {
  const borderBottom = isLast ? 'none' : `1px solid ${colors.gray150}`;

  if (layout === 'stack') {
    return (
      <div
        style={{
          paddingBottom: '0.9vh',
          marginBottom: isLast ? 0 : '0.9vh',
          borderBottom,
        }}
      >
        <span
          style={{
            ...labelBase,
            display: 'block',
            marginBottom: '0.6vh',
          }}
        >
          {label}
        </span>
        <div style={{ width: '100%', minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, min(42%, 11rem)) minmax(0, 1fr)',
        columnGap: 'clamp(8px, 1vh, 14px)',
        rowGap: '0.35vh',
        alignItems: 'start',
        paddingBottom: '0.9vh',
        marginBottom: isLast ? 0 : '0.9vh',
        borderBottom,
      }}
    >
      <span style={{ ...labelBase, paddingRight: '0.5vh' }}>{label}</span>
      <span
        style={{
          ...valueBase,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Tarjeta de datos (móvil/tablet): filas etiqueta + valor.
 * @param {{ label: string, value: React.ReactNode, layout?: 'inline'|'stack' }[]} rows
 */
export function DataCard({ rows = [], style, id, children }) {
  const n = rows.length;
  return (
    <div id={id} style={{ ...cardBaseStyle, ...style }}>
      {rows.map((row, i) => (
        <DataCardRow
          key={i}
          label={row.label}
          value={row.value}
          layout={row.layout || 'inline'}
          isLast={i === n - 1 && !children}
        />
      ))}
      {children}
    </div>
  );
}

/**
 * Lista vertical de tarjetas con separación uniforme.
 */
export function DataCardList({ children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5vh',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        alignSelf: 'stretch',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Contenedor de sección con márgenes laterales en vista compacta (tabla → tarjetas). */
export function DataCardSection({ children, compact, style }) {
  return (
    <div
      style={{
        marginTop: compact ? '1vh' : undefined,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingLeft: compact ? 'clamp(10px, 1.5vh, 20px)' : 0,
        paddingRight: compact ? 'clamp(10px, 1.5vh, 20px)' : 0,
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
